import { Children, isValidElement, useEffect, useState, type ReactNode } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { backFromState } from "@/lib/back-target";
import {
  ArrowLeft, Mail, Phone, MapPin, Trash2, ExternalLink, BanIcon, User, Cake, Eye, EyeOff, Merge, Flame,
} from "lucide-react";
import { Badge, Button, Skeleton, useConfirm, useToast } from "@/components/ui";
import { ContactInboxPanel } from "@/features/inbox/ContactInboxPanel";
import { ContactAvatarUpload } from "@/features/contacts/AvatarUpload";
import { MergeDialog } from "@/features/contacts/MergeDialog";
import { InlineEditField, InlineEditSelect, InlineEditCheckbox } from "@/features/contacts/InlineEdit";
import {
  AppointmentsPanel, AutomationsPanel, ConsentPanel, NotesPanel, OpportunitiesPanel, PaymentsPanel, TagsPanel, TasksPanel, TimelinePanel,
} from "@/features/contacts/InfoPanels";
import { WebsiteActivityPanel } from "@/features/contacts/WebsiteActivityPanel";
import { api, ApiError } from "@/lib/api";
import { fullName, formatPhone, formatDate, formatDateTime } from "@/lib/format";
import type { Contact, CustomField } from "@/lib/types";

const LIFECYCLE_OPTIONS = [
  { value: "lead",     label: "Lead" },
  { value: "contact",  label: "Contact" },
  { value: "customer", label: "Customer" },
  { value: "churned",  label: "Churned" },
];

const SOCIAL_NETWORKS: { key: string; label: string; placeholder: string }[] = [
  { key: "facebook",  label: "Facebook",   placeholder: "facebook.com/handle" },
  { key: "instagram", label: "Instagram",  placeholder: "@handle" },
  { key: "linkedin",  label: "LinkedIn",   placeholder: "linkedin.com/in/handle" },
  { key: "twitter",   label: "Twitter / X", placeholder: "@handle" },
  { key: "tiktok",    label: "TikTok",     placeholder: "@handle" },
  { key: "youtube",   label: "YouTube",    placeholder: "youtube.com/@handle" },
  { key: "whatsapp",  label: "WhatsApp",   placeholder: "+1…" },
  { key: "telegram",  label: "Telegram",   placeholder: "@handle" },
  { key: "website",   label: "Website",    placeholder: "https://…" },
];

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const back = backFromState(location.state);
  const toast = useToast();
  const confirm = useConfirm();
  const [contact, setContact] = useState<Contact | null>(null);
  // Custom fields list is workspace-wide so we fetch it once here for the
  // inline editor on the left rail. The right-rail panels (timeline,
  // tasks, payments, etc.) own their own data fetching via <InfoPanels>.
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [mergeOpen, setMergeOpen] = useState(false);
  // "Hide empty fields" toggle persists across contacts via localStorage.
  const [hideEmpty, setHideEmpty] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem("crm.contact.hideEmpty");
    return saved === null ? true : saved === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("crm.contact.hideEmpty", hideEmpty ? "1" : "0");
  }, [hideEmpty]);

  /**
   * Initial / silent reload of every panel on this page. `silent=true`
   * skips the loading spinner and error toasts so background polls don't
   * blink the rails when nothing has changed. We always replace the
   * canonical contact row but preserve the locally-rendered tag list (the
   * /contacts/:id endpoint doesn't include the joined tags) — tags are
   * managed via their own optimistic addTag/removeTag flow.
   */
  const load = async (silent = false) => {
    if (!id) return;
    try {
      const [c, cf] = await Promise.all([
        api.get<{ contact: Contact }>(`/contacts/${id}`),
        api.get<{ fields: CustomField[] }>("/custom-fields"),
      ]);
      setContact(c.contact);
      setCustomFields(cf.fields);
    } catch (err) {
      if (!silent) {
        toast.error(err instanceof ApiError ? err.message : "Failed to load contact");
        if (err instanceof ApiError && err.status === 404) navigate(back.to);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // Background poll so AI-driven updates (or another tab) propagate without
  // a manual refresh. The right-rail panels poll their own data internally.
  useEffect(() => {
    if (!id) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (timer) return; timer = setInterval(() => { void load(true); }, 15000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id]);

  /** Optimistic patch on the contact row itself. Tags are managed by TagsPanel. */
  const patch = async (delta: Partial<Contact>) => {
    if (!contact || !id) return;
    const prev = contact;
    setContact({ ...contact, ...delta } as Contact);
    try {
      const r = await api.patch<{ contact: Contact }>(`/contacts/${id}`, delta);
      setContact({ ...r.contact, tags: prev.tags });
    } catch (err) {
      setContact(prev);
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
      throw err;
    }
  };

  const onDelete = async () => {
    if (!id) return;
    const fullName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : null;
    if (!(await confirm({
      title: "Delete this contact?",
      description: (
        <>
          {fullName ? <strong className="text-zinc-100">{fullName}</strong> : "This contact"} and
          all their messages, tasks, notes, and activity will be permanently deleted.
          This can't be undone.
        </>
      ),
      confirmLabel: "Delete contact",
      danger: true,
    }))) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact deleted");
      navigate(back.to);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col p-3 sm:p-4 lg:p-6 space-y-4 overflow-hidden">
        <Skeleton className="h-8 w-48" />
        <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-12 min-h-0">
          <div className="space-y-3 lg:col-span-3"><Skeleton className="h-44" /><Skeleton className="h-32" /><Skeleton className="h-24" /></div>
          <Skeleton className="lg:col-span-6" />
          <div className="space-y-3 lg:col-span-3"><Skeleton className="h-32" /><Skeleton className="h-24" /></div>
        </div>
      </div>
    );
  }
  if (!contact) return null;

  return (
    // h-full + overflow-y-auto on mobile so the stacked rails scroll
    // naturally inside <main> (which is overflow-hidden in full-bleed
    // mode). On lg+ we lock scrolling here and let each column scroll
    // independently via `lg:overflow-y-auto` on the asides.
    <div className="flex h-full flex-col overflow-y-auto lg:overflow-hidden">
      {/* Slim top bar — back + delete only. The hide-empty toggle moved to
          its own card on the left rail. */}
      <div className="flex items-center gap-3 border-b border-zinc-800/70 px-3 sm:px-4 lg:px-6 py-2 shrink-0">
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate(back.to)}>
          <span className="hidden sm:inline">{back.label}</span>
        </Button>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" leftIcon={<Merge className="h-4 w-4" />} onClick={() => setMergeOpen(true)}>
          <span className="hidden sm:inline">Merge duplicate</span>
        </Button>
        <Button variant="ghost" size="sm" leftIcon={<Trash2 className="h-4 w-4 text-rose-400" />} onClick={onDelete} className="text-rose-300 hover:bg-rose-500/10">
          <span className="hidden sm:inline">Delete</span>
        </Button>
      </div>

      {/* Three-column body on lg+. On mobile the columns stack and scroll
          with the page (no nested scrolling); on lg+ each column manages
          its own vertical scroll and the window itself stays fixed-height. */}
      <div className="grid flex-1 grid-cols-1 gap-3 px-3 sm:px-4 lg:grid-cols-12 lg:gap-4 lg:px-6 py-3 lg:min-h-0">
        {/* Left rail */}
        <aside className="space-y-3 lg:overflow-y-auto lg:pr-1 lg:col-span-3">
          {/* Profile (name + clickable avatar for upload) */}
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 text-center">
            <ContactAvatarUpload
              contact={contact}
              onSaved={(c) => setContact({ ...c, tags: contact.tags })}
            />
            <h1 className="mt-3 text-base font-semibold text-zinc-100">
              {fullName(contact) || <span className="italic text-zinc-500">Unnamed</span>}
            </h1>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <LeadScoreBadge score={contact.lead_score} />
              {contact.do_not_contact && (
                <Badge color="rose" size="sm"><BanIcon className="h-3 w-3 mr-1" /> Do not contact</Badge>
              )}
            </div>
          </div>

          {/* Hide-empty-fields toggle in its own card so it's visible at a glance */}
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-3">
            <label className="flex cursor-pointer items-center gap-2.5 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-rose-500"
              />
              {hideEmpty ? <EyeOff className="h-3.5 w-3.5 text-zinc-400" /> : <Eye className="h-3.5 w-3.5 text-zinc-400" />}
              <span className="flex-1">Hide empty fields</span>
            </label>
          </div>

          {/* Identity */}
          <Card title="Identity">
            <Row icon={User} label="First name" hidden={hideEmpty && !contact.first_name}>
              <InlineEditField value={contact.first_name} onSave={(v) => patch({ first_name: v })} label="First name" />
            </Row>
            <Row icon={User} label="Last name" hidden={hideEmpty && !contact.last_name}>
              <InlineEditField value={contact.last_name} onSave={(v) => patch({ last_name: v })} label="Last name" />
            </Row>
            <Row icon={Mail} label="Email" hidden={hideEmpty && !contact.email}>
              <InlineEditField value={contact.email} onSave={(v) => patch({ email: v })} kind="email" label="Email" />
            </Row>
            <Row icon={Phone} label="Phone" hidden={hideEmpty && !contact.phone}>
              <InlineEditField value={contact.phone} onSave={(v) => patch({ phone: v })} kind="tel" label="Phone" formatDisplay={(v) => formatPhone(v)} />
            </Row>
            <Row icon={Cake} label="Birthday" hidden={hideEmpty && !contact.date_of_birth}>
              <InlineEditField value={contact.date_of_birth} onSave={(v) => patch({ date_of_birth: v })} kind="date" label="Date of birth" formatDisplay={(v) => formatDate(v)} />
            </Row>
          </Card>

          {/* Lifecycle — stage always shown (it's never empty) */}
          <Card title="Lifecycle">
            <Row label="Stage">
              <InlineEditSelect value={contact.lifecycle_stage ?? "lead"} options={LIFECYCLE_OPTIONS} onSave={(v) => patch({ lifecycle_stage: v })} label="Lifecycle stage" />
            </Row>
            <Row label="Source" hidden={hideEmpty && !contact.source}>
              <InlineEditField value={contact.source} onSave={(v) => patch({ source: v })} label="Source" placeholder="manual, form, import…" />
            </Row>
            {/* The legacy "Do not contact" checkbox lived here. The canonical
             *  master kill-switch is now the "Do not disturb / Block all
             *  channels" toggle in the Communication-channels card, backed
             *  by contact_consent (channel='all'). The DB still keeps
             *  contacts.do_not_contact in sync for back-compat with older
             *  send paths, so toggling DND there flips both. */}
          </Card>

          {/* Address */}
          <Card title="Address">
            <Row icon={MapPin} label="Street" hidden={hideEmpty && !contact.address_line1}>
              <InlineEditField value={contact.address_line1} onSave={(v) => patch({ address_line1: v })} label="Street" />
            </Row>
            <Row label="City" hidden={hideEmpty && !contact.city}>
              <InlineEditField value={contact.city} onSave={(v) => patch({ city: v })} label="City" />
            </Row>
            <Row label="State / Region" hidden={hideEmpty && !contact.state}>
              <InlineEditField value={contact.state} onSave={(v) => patch({ state: v })} label="State / Region" />
            </Row>
            <Row label="Postal code" hidden={hideEmpty && !contact.postal_code}>
              <InlineEditField value={contact.postal_code} onSave={(v) => patch({ postal_code: v })} label="Postal code" />
            </Row>
            <Row label="Country" hidden={hideEmpty && !contact.country}>
              <InlineEditField value={contact.country} onSave={(v) => patch({ country: v })} label="Country" />
            </Row>
          </Card>

          {/* Social profiles */}
          <Card title="Social profiles">
            {SOCIAL_NETWORKS.map(({ key, label, placeholder }) => {
              const value = contact.socials[key] ?? "";
              const isUrl = /^https?:/.test(value);
              return (
                <Row key={key} label={label} hidden={hideEmpty && !value}>
                  <div className="flex items-center gap-1">
                    <div className="flex-1 min-w-0">
                      <InlineEditField
                        value={value}
                        onSave={(v) => patch({ socials: { ...contact.socials, [key]: v ?? "" } })}
                        kind="url"
                        label={label}
                        placeholder={placeholder}
                      />
                    </div>
                    {isUrl && (
                      <a href={value} target="_blank" rel="noreferrer" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300" aria-label={`Open ${label}`}>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </Row>
              );
            })}
          </Card>

          {/* Custom fields */}
          {customFields.length > 0 && (
            <Card title="Custom fields">
              {customFields.map((f) => {
                const v = contact.custom_fields[f.key];
                const isEmpty = v == null || v === "" || (Array.isArray(v) && v.length === 0);
                return (
                  <Row key={f.key} label={f.label} hidden={hideEmpty && isEmpty}>
                    <CustomFieldInlineEdit
                      field={f}
                      value={v}
                      onSave={(next) => patch({ custom_fields: { ...contact.custom_fields, [f.key]: next } })}
                    />
                  </Row>
                );
              })}
            </Card>
          )}

          {/* Per-channel do-not-disturb sits at the bottom of the left rail,
              alongside the contact's identity fields, since it's a property
              *of* the contact rather than activity. */}
          <ConsentPanel contact={contact} />
        </aside>

        {/* Center: shared chat panel — fills available column height on
            desktop; on mobile gets a fixed min height so the composer is
            always reachable inside a stacked layout. */}
        <section className="lg:col-span-6 min-h-112 lg:min-h-0">
          <ContactInboxPanel contact={contact} />
        </section>

        {/* Right rail — every panel here is the same component the inbox
            info rail uses, so the two stay in lockstep. */}
        <aside className="space-y-3 lg:overflow-y-auto lg:pr-1 lg:col-span-3">
          <TimelinePanel contactId={contact.id} />
          {/* What they browsed on your sites — collapses to nothing for the
              (majority of) contacts with no linked web activity. */}
          <WebsiteActivityPanel contactId={contact.id} />
          <TasksPanel contactId={contact.id} />
          <TagsPanel contact={contact} onContactChange={(c) => setContact(c)} />
          <NotesPanel contactId={contact.id} />
          <AppointmentsPanel contactId={contact.id} />
          <OpportunitiesPanel contactId={contact.id} />
          <PaymentsPanel contactId={contact.id} />
          <AutomationsPanel contactId={contact.id} />
        </aside>
      </div>

      {/* Merge a duplicate contact INTO this one. The current contact is the
          survivor; on success we silently reload every rail so the merged-in
          messages, tasks, notes, and activity appear. */}
      <MergeDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        winner={contact}
        onMerged={() => { void load(true); }}
      />
    </div>
  );
}

/**
 * Small engagement-heat indicator. lead_score is 0–100; we bucket it into
 * cold (zinc), warm (amber), and hot (emerald) so the color reads at a
 * glance without the user having to interpret the number.
 */
function LeadScoreBadge({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(score ?? 0)));
  const color = clamped >= 67 ? "emerald" : clamped >= 34 ? "amber" : "zinc";
  return (
    <Badge color={color} size="sm">
      <Flame className="h-3 w-3 mr-1" /> Lead score {clamped}
    </Badge>
  );
}

// ─── Building blocks for the rails ────────────────────────────────────────
/**
 * Card collapses entirely when every Row inside is hidden. We can't just
 * `filter(Boolean)` the children — Row elements are always truthy regardless
 * of their `hidden` prop. Instead we walk the children, peek at each one's
 * `hidden` prop, and treat the card as empty when every Row passed
 * `hidden=true`.
 */
function Card({ title, children }: { title: string; children: ReactNode }) {
  const arr = Children.toArray(children);
  const anyVisible = arr.some((child) => {
    if (isValidElement(child)) {
      const props = child.props as { hidden?: boolean };
      return !props.hidden;
    }
    return Boolean(child);
  });
  if (!anyVisible) return null;
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/**
 * One field row with the label stacked ABOVE the editor (per the requested
 * layout). Setting `hidden` returns null, which Card detects via the
 * children walk above to collapse the whole section if nothing is showing.
 */
function Row({ icon: Icon, label, children, hidden }: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  children: ReactNode;
  hidden?: boolean;
}) {
  if (hidden) return null;
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CustomFieldInlineEdit({ field, value, onSave }: {
  field: CustomField;
  value: unknown;
  onSave: (next: unknown) => Promise<void>;
}) {
  switch (field.field_type) {
    case "boolean":
      return (
        <InlineEditCheckbox
          value={!!value}
          onSave={(v) => onSave(v)}
          label={value ? "Yes" : "No"}
        />
      );
    case "longtext":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          kind="longtext"
          label={field.label}
        />
      );
    case "number":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v == null ? null : Number(v))}
          kind="number"
          label={field.label}
        />
      );
    case "date":
      return (
        <InlineEditField
          value={value == null ? null : String(value).slice(0, 10)}
          onSave={(v) => onSave(v)}
          kind="date"
          label={field.label}
          formatDisplay={(v) => formatDate(v)}
        />
      );
    case "datetime":
      return (
        <InlineEditField
          value={value == null ? null : String(value).slice(0, 16)}
          onSave={(v) => onSave(v)}
          kind="datetime"
          label={field.label}
          formatDisplay={(v) => formatDateTime(v)}
        />
      );
    case "url":
    case "email":
    case "phone":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          kind={field.field_type === "phone" ? "tel" : field.field_type}
          label={field.label}
        />
      );
    case "dropdown":
      return (
        <InlineEditSelect
          value={value == null ? "" : String(value)}
          options={[
            { value: "", label: "—" },
            ...field.options.map((o) => ({ value: o, label: o })),
          ]}
          onSave={(v) => onSave(v === "" ? null : v)}
          label={field.label}
        />
      );
    case "multiselect": {
      const current = Array.isArray(value) ? (value as string[]).join(", ") : "";
      return (
        <InlineEditField
          value={current}
          onSave={(v) => onSave(v == null ? [] : v.split(",").map((s) => s.trim()).filter(Boolean))}
          label={field.label}
          placeholder="comma-separated"
        />
      );
    }
    default:
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          label={field.label}
        />
      );
  }
}

